import { useEffect, useMemo, useRef } from "react"

import { FleetB2BPageShell } from "@/features/operator/fleet/fleet-b2b-page-shell"
import { applyProvisionedFleetDraft } from "@/features/operator/fleet/fleet-onboarding-run-hydration"
import {
  applyFleetOnboardingRouteContext,
  createFleetOperatingUnitDraft,
  FLEET_ONBOARDING_MODE_LABELS,
  FLEET_VERTICAL_LABELS,
  type FleetOnboardingMode,
  type FleetOnboardingSearch,
  type FleetVertical,
  resolveFleetOnboardingMode,
  resolveFleetOnboardingVertical,
  summarizeFleetOnboardingDraft,
  useFleetOnboardingDraft,
} from "@/features/operator/fleet/fleet-onboarding-shared"
import { FleetOnboardingStepper } from "@/features/operator/fleet/fleet-onboarding-stepper"
import { getOperatorSessionOrganization } from "@/features/operator/shared/operator-access"
import { OperatorPanel, OperatorStatusMessage } from "@/features/operator/shared/operator-shell"
import {
  useOptionalOperatorShellSession,
} from "@/features/operator/shared/operator-shell-session-context"
import { apiClient } from "@/lib/api/client"

interface FleetOnboardingOrganizationPageProps {
  onContinue: (selection: {
    mode: FleetOnboardingMode
    vertical: FleetVertical
  }) => void
  search: FleetOnboardingSearch
}

export function FleetOnboardingOrganizationPage({
  onContinue,
  search,
}: FleetOnboardingOrganizationPageProps) {
  const shellSession = useOptionalOperatorShellSession()
  const { draft, setDraft } = useFleetOnboardingDraft()
  const summary = summarizeFleetOnboardingDraft(draft)
  const hydratedContextRef = useRef<string | null>(null)

  useEffect(() => {
    setDraft((previous) =>
      applyFleetOnboardingRouteContext(previous, {
        mode: search.mode,
        organizationId: search.organizationId,
        runId: search.runId,
        vertical: search.vertical,
      })
    )
  }, [search.mode, search.organizationId, search.runId, search.vertical, setDraft])

  const selectedVertical = resolveFleetOnboardingVertical(search.vertical, draft)
  const selectedMode = resolveFleetOnboardingMode(search.mode, draft, search.step)
  const sessionOrganizationId = useMemo(() => {
    if (shellSession?.status !== "ready") {
      return null
    }

    return getOperatorSessionOrganization(shellSession.session).id
  }, [shellSession])

  useEffect(() => {
    const targetOrganizationId =
      search.organizationId || draft.activeOrganizationId || sessionOrganizationId || undefined
    const targetRunId = search.runId || draft.activeRunId || undefined
    const contextKey = `${targetOrganizationId || "session"}:${targetRunId || "active"}`
    if (hydratedContextRef.current === contextKey) {
      return
    }

    let isActive = true
    void apiClient
      .resolveFleetOnboardingRun({
        onboardingMode: selectedMode,
        organizationId: targetOrganizationId,
        runId: targetRunId,
        step: "organization",
        vertical: selectedVertical,
      })
      .then((resolved) => {
        if (!isActive) {
          return
        }

        hydratedContextRef.current = contextKey
        setDraft((previous) => {
          const withRouteContext = applyFleetOnboardingRouteContext(previous, {
            mode: resolved.run.onboardingMode || selectedMode,
            organizationId: resolved.run.organizationId,
            runId: resolved.run.id,
            vertical: resolved.run.vertical || selectedVertical,
          })
          const ownerProfile =
            shellSession?.status === "ready" && shellSession.session.actorType === "user"
              ? shellSession.session.profile
              : null

          return applyProvisionedFleetDraft(
            withRouteContext,
            resolved.organization,
            ownerProfile
          )
        })
      })
      .catch(() => undefined)

    return () => {
      isActive = false
    }
  }, [
    draft.activeOrganizationId,
    draft.activeRunId,
    search.organizationId,
    search.runId,
    selectedMode,
    selectedVertical,
    sessionOrganizationId,
    setDraft,
    shellSession,
  ])

  function updateDraftField(
    key: Exclude<keyof typeof draft, "units">,
    value: string
  ) {
    setDraft((previous) => ({
      ...previous,
      kitIssuance: null,
      masterPersistence: null,
      [key]: value,
    }))
  }

  function updateUnitField(
    unitId: string,
    key: "city" | "code" | "escalationPhone" | "managerName" | "managerPhone" | "name" | "region" | "type",
    value: string
  ) {
    setDraft((previous) => ({
      ...previous,
      kitIssuance: null,
      masterPersistence: null,
      units: previous.units.map((unit) =>
        unit.id === unitId
          ? {
              ...unit,
              [key]: value,
            }
          : unit
      ),
    }))
  }

  function addOperatingUnit() {
    setDraft((previous) => ({
      ...previous,
      kitIssuance: null,
      masterPersistence: null,
      units: [
        ...previous.units,
        createFleetOperatingUnitDraft({
          type: previous.units[0]?.type || "branch",
        }),
      ],
    }))
  }

  function removeOperatingUnit(unitId: string) {
    setDraft((previous) => {
      if (previous.units.length <= 1) {
        return previous
      }

      const nextUnits = previous.units.filter((unit) => unit.id !== unitId)
      if (!nextUnits.some((unit) => unit.isPrimary) && nextUnits[0]) {
        nextUnits[0] = {
          ...nextUnits[0],
          isPrimary: true,
        }
      }

      return {
        ...previous,
        kitIssuance: null,
        masterPersistence: null,
        units: nextUnits,
      }
    })
  }

  function setPrimaryOperatingUnit(unitId: string) {
    setDraft((previous) => ({
      ...previous,
      kitIssuance: null,
      masterPersistence: null,
      units: previous.units.map((unit) => ({
        ...unit,
        isPrimary: unit.id === unitId,
      })),
    }))
  }

  return (
    <FleetB2BPageShell
      activeSection="onboarding"
      title="Organization and operating-unit setup"
      description="Define the transporter, fleet operator, or delivery company first, then capture branches, depots, hubs, terminals, or route bases as first-class operating units."
      highlights={[
        "Organization profile, business vertical, and enterprise contact ownership.",
        "Multi-branch and multi-depot operating-unit setup before asset onboarding.",
        "Language defaults, emergency contacts, and regional operating context.",
      ]}
    >
      <div className="grid gap-5">
        <OperatorPanel
          title="Onboarding progress"
          description="This page now owns the first real working step of the Fleet B2B flow."
        >
          <FleetOnboardingStepper activeStep="organization" />
        </OperatorPanel>

        <OperatorStatusMessage>
          Vertical: <strong>{FLEET_VERTICAL_LABELS[selectedVertical]}</strong>. Next working surface:
          <strong> {FLEET_ONBOARDING_MODE_LABELS[selectedMode]}</strong>.
        </OperatorStatusMessage>

        <section className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,0.95fr)]">
          <OperatorPanel
            title="Organization profile"
            description="Capture the enterprise owner before asset rows, assignments, and kits are generated."
          >
            <div className="grid gap-3 md:grid-cols-2">
              <label className="grid gap-2 text-sm">
                <span className="text-muted-foreground">Organization name</span>
                <input
                  className="rounded-2xl border border-border bg-background px-3 py-2"
                  value={draft.organizationName}
                  onChange={(event) => updateDraftField("organizationName", event.target.value)}
                  placeholder="Grobet India Agrotech Fleet"
                />
              </label>
              <label className="grid gap-2 text-sm">
                <span className="text-muted-foreground">Legal name</span>
                <input
                  className="rounded-2xl border border-border bg-background px-3 py-2"
                  value={draft.legalName}
                  onChange={(event) => updateDraftField("legalName", event.target.value)}
                  placeholder="Grobet India Agrotech Private Limited"
                />
              </label>
              <label className="grid gap-2 text-sm">
                <span className="text-muted-foreground">Primary admin name</span>
                <input
                  className="rounded-2xl border border-border bg-background px-3 py-2"
                  value={draft.adminName}
                  onChange={(event) => updateDraftField("adminName", event.target.value)}
                  placeholder="Fleet operations owner"
                />
              </label>
              <label className="grid gap-2 text-sm">
                <span className="text-muted-foreground">Primary admin email</span>
                <input
                  className="rounded-2xl border border-border bg-background px-3 py-2"
                  type="email"
                  value={draft.adminEmail}
                  onChange={(event) => updateDraftField("adminEmail", event.target.value)}
                  placeholder="ops@fleetco.com"
                />
              </label>
              <label className="grid gap-2 text-sm">
                <span className="text-muted-foreground">Primary admin phone</span>
                <input
                  className="rounded-2xl border border-border bg-background px-3 py-2"
                  value={draft.adminPhone}
                  onChange={(event) => updateDraftField("adminPhone", event.target.value)}
                  placeholder="+919900001111"
                />
              </label>
              <label className="grid gap-2 text-sm">
                <span className="text-muted-foreground">Primary city</span>
                <input
                  className="rounded-2xl border border-border bg-background px-3 py-2"
                  value={draft.primaryCity}
                  onChange={(event) => updateDraftField("primaryCity", event.target.value)}
                  placeholder="Bengaluru"
                />
              </label>
              <label className="grid gap-2 text-sm">
                <span className="text-muted-foreground">Primary country</span>
                <input
                  className="rounded-2xl border border-border bg-background px-3 py-2"
                  value={draft.primaryCountry}
                  onChange={(event) => updateDraftField("primaryCountry", event.target.value)}
                  placeholder="India"
                />
              </label>
              <label className="grid gap-2 text-sm">
                <span className="text-muted-foreground">Operating regions</span>
                <input
                  className="rounded-2xl border border-border bg-background px-3 py-2"
                  value={draft.operatingRegions}
                  onChange={(event) => updateDraftField("operatingRegions", event.target.value)}
                  placeholder="South India corridor"
                />
              </label>
              <label className="grid gap-2 text-sm">
                <span className="text-muted-foreground">Preferred public language</span>
                <input
                  className="rounded-2xl border border-border bg-background px-3 py-2"
                  value={draft.preferredPublicLanguage}
                  onChange={(event) =>
                    updateDraftField("preferredPublicLanguage", event.target.value)
                  }
                />
              </label>
              <label className="grid gap-2 text-sm">
                <span className="text-muted-foreground">Preferred driver / rider language</span>
                <input
                  className="rounded-2xl border border-border bg-background px-3 py-2"
                  value={draft.preferredDriverLanguage}
                  onChange={(event) =>
                    updateDraftField("preferredDriverLanguage", event.target.value)
                  }
                />
              </label>
            </div>
          </OperatorPanel>

          <OperatorPanel
            title="Onboarding readiness"
            description="This is the current completeness snapshot before import or manual roster work begins."
          >
            <div className="grid gap-3">
              <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                <div className="text-sm text-muted-foreground">Completed organization fields</div>
                <div className="mt-2 text-2xl font-semibold">
                  {summary.completedOrganizationFields}
                </div>
              </div>
              <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                <div className="text-sm text-muted-foreground">Complete operating units</div>
                <div className="mt-2 text-2xl font-semibold">
                  {summary.completeUnits} / {summary.totalUnits}
                </div>
              </div>
              <OperatorStatusMessage>
                Save enough organization context here so the import or manual roster phase can
                attach assets, people, and assignments to a real operating structure.
              </OperatorStatusMessage>
            </div>
          </OperatorPanel>
        </section>

        <OperatorPanel
          title="Operating units"
          description="Capture one or more branches, depots, hubs, terminals, or route bases before asset onboarding begins."
        >
          <div className="grid gap-4">
            {draft.units.map((unit, index) => (
              <div
                key={unit.id}
                className="rounded-3xl border border-border/70 bg-background/70 px-4 py-4"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold">
                      {unit.name.trim() || `Operating unit ${index + 1}`}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {unit.code.trim() || "No code yet"} • {unit.type || "branch"}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setPrimaryOperatingUnit(unit.id)}
                      className="rounded-2xl border border-border px-3 py-2 text-xs font-medium"
                    >
                      {unit.isPrimary ? "Primary unit" : "Make primary"}
                    </button>
                    <button
                      type="button"
                      onClick={() => removeOperatingUnit(unit.id)}
                      disabled={draft.units.length === 1}
                      className="rounded-2xl border border-border px-3 py-2 text-xs font-medium disabled:opacity-50"
                    >
                      Remove
                    </button>
                  </div>
                </div>

                <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                  <label className="grid gap-2 text-sm">
                    <span className="text-muted-foreground">Unit code</span>
                    <input
                      className="rounded-2xl border border-border bg-background px-3 py-2"
                      value={unit.code}
                      onChange={(event) =>
                        updateUnitField(unit.id, "code", event.target.value.toUpperCase())
                      }
                      placeholder="BLR-HQ"
                    />
                  </label>
                  <label className="grid gap-2 text-sm">
                    <span className="text-muted-foreground">Unit name</span>
                    <input
                      className="rounded-2xl border border-border bg-background px-3 py-2"
                      value={unit.name}
                      onChange={(event) => updateUnitField(unit.id, "name", event.target.value)}
                      placeholder="Bengaluru HQ"
                    />
                  </label>
                  <label className="grid gap-2 text-sm">
                    <span className="text-muted-foreground">Unit type</span>
                    <select
                      className="rounded-2xl border border-border bg-background px-3 py-2"
                      value={unit.type}
                      onChange={(event) => updateUnitField(unit.id, "type", event.target.value)}
                    >
                      <option value="branch">Branch</option>
                      <option value="depot">Depot</option>
                      <option value="hub">Hub</option>
                      <option value="terminal">Terminal</option>
                      <option value="route_base">Route base</option>
                    </select>
                  </label>
                  <label className="grid gap-2 text-sm">
                    <span className="text-muted-foreground">City</span>
                    <input
                      className="rounded-2xl border border-border bg-background px-3 py-2"
                      value={unit.city}
                      onChange={(event) => updateUnitField(unit.id, "city", event.target.value)}
                      placeholder="Bengaluru"
                    />
                  </label>
                  <label className="grid gap-2 text-sm">
                    <span className="text-muted-foreground">Region / state</span>
                    <input
                      className="rounded-2xl border border-border bg-background px-3 py-2"
                      value={unit.region}
                      onChange={(event) => updateUnitField(unit.id, "region", event.target.value)}
                      placeholder="Karnataka"
                    />
                  </label>
                  <label className="grid gap-2 text-sm">
                    <span className="text-muted-foreground">Manager name</span>
                    <input
                      className="rounded-2xl border border-border bg-background px-3 py-2"
                      value={unit.managerName}
                      onChange={(event) =>
                        updateUnitField(unit.id, "managerName", event.target.value)
                      }
                      placeholder="Branch manager"
                    />
                  </label>
                  <label className="grid gap-2 text-sm">
                    <span className="text-muted-foreground">Manager phone</span>
                    <input
                      className="rounded-2xl border border-border bg-background px-3 py-2"
                      value={unit.managerPhone}
                      onChange={(event) =>
                        updateUnitField(unit.id, "managerPhone", event.target.value)
                      }
                      placeholder="+919900001234"
                    />
                  </label>
                  <label className="grid gap-2 text-sm">
                    <span className="text-muted-foreground">Escalation phone</span>
                    <input
                      className="rounded-2xl border border-border bg-background px-3 py-2"
                      value={unit.escalationPhone}
                      onChange={(event) =>
                        updateUnitField(unit.id, "escalationPhone", event.target.value)
                      }
                      placeholder="+919900009999"
                    />
                  </label>
                </div>
              </div>
            ))}

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={addOperatingUnit}
                className="rounded-2xl border border-border px-4 py-2 text-sm font-medium"
              >
                Add another operating unit
              </button>
              <button
              type="button"
              onClick={() =>
                onContinue({
                  mode: selectedMode,
                  vertical: selectedVertical,
                })
              }
              className="rounded-2xl bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
            >
              Continue to {FLEET_ONBOARDING_MODE_LABELS[selectedMode]}
              </button>
            </div>
          </div>
        </OperatorPanel>
      </div>
    </FleetB2BPageShell>
  )
}
